import { StatusCodes } from "http-status-codes";
import { TaJson } from "ta-json";
import URI from "urijs";

import { MapNumberTo, MapStringTo, Nullable, NullableResultPromise } from "../base-types";
import { EntityDefinitionCache } from "../caches/entity-definition-cache";
import { ENTITY_DEFINITIONS } from "../constants/api";
import { IEntityDefinition } from "../contracts/base/entity-definition";
import { ViewMode } from "../contracts/base/view-mode";
import { EntityDefinitionIdIterator } from "../contracts/querying/entity-definition-id-iterator";
import { EntityDefinitionIdQueryResult } from "../contracts/querying/entity-definition-id-query-result";
import { EntityDefinitionIterator, IEntityDefinitionIterator } from "../contracts/querying/entity-definition-iterator";
import {
    EntityDefinitionQueryResult,
    IEntityDefinitionQueryResult,
} from "../contracts/querying/entity-definition-query-result";
import { IIdIterator } from "../contracts/querying/id-iterator";
import { IIdQueryResult } from "../contracts/querying/id-query-result";
import { InternalError } from "../errors/internal-error";
import Guard from "../guard";
import Link from "../link";
import { EntityDefinitionCollectionResource } from "../models/entity-definition-collection-resource";
import { EntityDefinitionResource } from "../models/entity-definition-resource";
import { TypeGuards } from "../type-guards";
import { chunk } from "../utilities/array-utilities";
import { IExtendedContentHubClient } from "./extended-client";
import { ResponseHandler } from "./response-handler";
import { ResponseMessage } from "./response-message";

export interface IEntityDefinitionsClient {
    // Single
    /**
     * Get the {@link IEntityDefinition} instance by the specified name.
     * @param name - The name of the definition
     * @param includeConditionalMembers - Include conditional members
     * @param loadPermissions - Load permissions for the definition
     * @param cacheSignature - Signature of the cached resources for controlling browser caching
     * @returns Promise which resolves to an {@link IEntityDefinition} or null.
     */
    getAsync(
        name: string,
        includeConditionalMembers?: boolean,
        loadPermissions?: boolean,
        cacheSignature?: string
    ): NullableResultPromise<IEntityDefinition>;

    /**
     * Get the {@link IEntityDefinition} instance by the specified id.
     * @param id - The id of the definition
     * @param includeConditionalMembers - Include conditional members
     * @param loadPermissions - Load permissions for the definition
     * @param cacheSignature - Signature of the cached resources for controlling browser caching
     * @returns Promise which resolves to an {@link IEntityDefinition} or null.
     */
    getAsync(
        id: number,
        includeConditionalMembers?: boolean,
        loadPermissions?: boolean,
        cacheSignature?: string
    ): NullableResultPromise<IEntityDefinition>;

    /**
     * Get all the {@link IEntityDefinition}s.
     * @param includeConditionalMembers - Include conditional members
     * @param loadPermissions - Load permissions for the definitions
     * @param viewMode - Select appropriate ViewMode
     * @param loadPermissions - Load permissions for the definitions
     * @param excludeTaxonomyDefinitions - Exclude taxonomy definitions from the result
     * @param excludeSystemOwnedDefinitions - Exclude system owned definitions from the result
     * @param filter - Additional filters
     * @param definitionsToLoadByName - Comma separated list of definition names
     * @param definitionsToLoadById - Comma separated list of definition identifiers
     * @param excludeLightDefinitions - Exclude light definitions from the result
     * @returns Promise which resolved to an array of {@link IEntityDefinition}s.
     */
    getAllAsync(
        includeConditionalMembers?: boolean,
        loadPermissions?: boolean,
        viewMode?: ViewMode,
        excludeTaxonomyDefinitions?: boolean,
        excludeSystemOwnedDefinitions?: boolean,
        filter?: string,
        definitionsToLoadByName?: string,
        definitionsToLoadById?: string,
        excludeLightDefinitions?: boolean
    ): Promise<Array<IEntityDefinition>>;

    /**
     * Get all the {@link IEntityDefinition}s (cached).
     * @param includeConditionalMembers - Include conditional members
     * @param loadPermissions - Load permissions for the definitions
     * @param excludeTaxonomyDefinitions - Exclude taxonomy definitions from the result
     * @param excludeSystemOwnedDefinitions - Exclude system owned definitions from the result
     * @param filter - Additional filters
     * @param definitionsToLoadByName - Comma separated list of definition names
     * @param definitionsToLoadById - Comma separated list of definition identifiers
     * @param excludeLightDefinitions - Exclude light definitions from the result
     * @returns Promise which resolved to an array of {@link IEntityDefinition}s.
     */
    getAllCachedAsync(
        includeConditionalMembers?: boolean,
        loadPermissions?: boolean,
        viewMode?: ViewMode,
        excludeTaxonomyDefinitions?: boolean,
        excludeSystemOwnedDefinitions?: boolean,
        filter?: string,
        definitionsToLoadByName?: string,
        definitionsToLoadById?: string,
        excludeLightDefinitions?: boolean
    ): Promise<Array<IEntityDefinition>>;

    /**
     * Get the cached {@link IEntityDefinition} instance by the specified name.
     * @param name - The name of the definition
     * @param includeConditionalMembers - Include conditional members
     * @param loadPermissions - Load permissions for the definition
     * @param cacheSignature - Signature of the cached resources for controlling browser caching
     * @returns Promise which resolves to an {@link IEntityDefinition} or null.
     */
    getCachedAsync(
        name: string,
        includeConditionalMembers?: boolean,
        loadPermissions?: boolean,
        cacheSignature?: string
    ): NullableResultPromise<IEntityDefinition>;

    getCachedAsync(
        id: number,
        includeConditionalMembers?: boolean,
        loadPermissions?: boolean,
        cacheSignature?: string
    ): NullableResultPromise<IEntityDefinition>;

    getNameAsync(id: number, cacheSignature?: string): NullableResultPromise<string>;

    getNameCachedAsync(id: number, cacheSignature?: string): NullableResultPromise<string>;

    getIdAsync(name: string, cacheSignature?: string): NullableResultPromise<number>;

    getIdCachedAsync(name: string, cacheSignature?: string): NullableResultPromise<number>;

    // Many
    /**
     * Get multiple entity definitions.
     * @param names - Names of the definitions to fetch
     * @param includeConditionalMembers - Include conditional members
     * @param loadPermissions - Load permissions for the definitions
     * @returns A list of entity definitions.
     */
    getManyAsync(
        names: Array<string>,
        includeConditionalMembers?: boolean,
        loadPermissions?: boolean
    ): Promise<Array<IEntityDefinition>>;

    /**
     * Get multiple entity definitions.
     * @param ids - The ids of the definitions to fetch
     * @param includeConditionalMembers - Include conditional members
     * @param loadPermissions - Load permissions for the definitions
     * @returns A list of entity definitions.
     */
    getManyAsync(
        ids: Array<number>,
        includeConditionalMembers?: boolean,
        loadPermissions?: boolean
    ): Promise<Array<IEntityDefinition>>;

    /**
     * Get multiple entity definitions. Check the cache before making a request.
     * @param names - Names of the definitions to fetch
     * @param includeConditionalMembers - Include conditional members
     * @param loadPermissions - Load permissions for the definitions
     * @returns A list of entity definitions.
     */
    getManyCachedAsync(
        names: Array<string>,
        includeConditionalMembers?: boolean,
        loadPermissions?: boolean
    ): Promise<Array<IEntityDefinition>>;

    /**
     * Get multiple entity definitions. Check the cache before making a request.
     * @param ids - The ids of the definitions to fetch
     * @param includeConditionalMembers - Include conditional members
     * @param loadPermissions - Load permissions for the definitions
     * @returns A list of entity definitions.
     */
    getManyCachedAsync(
        ids: Array<number>,
        includeConditionalMembers?: boolean,
        loadPermissions?: boolean
    ): Promise<Array<IEntityDefinition>>;

    getNamesAsync(ids: Array<number>): Promise<MapNumberTo<string>>;

    getNamesCachedAsync(ids: Array<number>): Promise<MapNumberTo<string>>;

    getIdsAsync(names: Array<string>): Promise<MapStringTo<number>>;

    getIdsCachedAsync(names: Array<string>): Promise<MapStringTo<number>>;

    // Operations
    saveAsync(definition: IEntityDefinition): Promise<number>;

    deleteAsync(name: string): Promise<void>;

    deleteAsync(id: number): Promise<void>;

    // Range
    getRangeAsync(skip: number /* = default */, take: number /* = default */): Promise<IEntityDefinitionQueryResult>;

    getRangeIdsAsync(skip: number /* = default */, take: number /* = default */): Promise<IIdQueryResult>;

    // Iterators
    createEntityDefinitionIterator(pageSize: number /* = default */): IEntityDefinitionIterator;

    createEntityDefinitionIdIterator(pageSize: number /* = default */): IIdIterator;
}

export class EntityDefinitionsClient implements IEntityDefinitionsClient {
    private static _batchSize = 25;

    private readonly _client: IExtendedContentHubClient;
    private readonly _cache: EntityDefinitionCache;

    constructor(client: IExtendedContentHubClient) {
        this._client = client;
        this._cache = new EntityDefinitionCache();
    }

    async getAllCachedAsync(
        includeConditionalMembers: boolean = true,
        loadPermissions?: boolean,
        viewMode?: ViewMode,
        excludeTaxonomyDefinitions?: boolean,
        excludeSystemOwnedDefinitions?: boolean,
        filter?: string,
        definitionsToLoadByName?: string,
        definitionsToLoadById?: string,
        excludeLightDefinitions?: boolean
    ): Promise<Array<IEntityDefinition>> {
        let definitions = this._cache.getAll(
            undefined,
            includeConditionalMembers,
            loadPermissions,
            viewMode,
            excludeTaxonomyDefinitions,
            excludeSystemOwnedDefinitions,
            filter,
            definitionsToLoadByName,
            definitionsToLoadById,
            excludeLightDefinitions
        );

        if (!definitions) {
            definitions = await this.getAllAsync(
                includeConditionalMembers,
                loadPermissions,
                viewMode,
                excludeTaxonomyDefinitions,
                excludeSystemOwnedDefinitions,
                filter,
                definitionsToLoadByName,
                definitionsToLoadById,
                excludeLightDefinitions
            );
        }

        return definitions;
    }

    async getAllAsync(
        includeConditionalMembers: boolean = true,
        loadPermissions?: boolean,
        viewMode?: ViewMode,
        excludeTaxonomyDefinitions?: boolean,
        excludeSystemOwnedDefinitions?: boolean,
        filter?: string,
        definitionsToLoadByName?: string,
        definitionsToLoadById?: string,
        excludeLightDefinitions?: boolean
    ): Promise<Array<IEntityDefinition>> {
        const allEntityDefinitions = await this.getAllBatchedAsync(
            includeConditionalMembers,
            loadPermissions,
            viewMode,
            excludeTaxonomyDefinitions,
            excludeSystemOwnedDefinitions,
            filter,
            definitionsToLoadByName,
            definitionsToLoadById,
            excludeLightDefinitions
        );

        this._cache.loadAll(
            allEntityDefinitions,
            undefined,
            undefined,
            includeConditionalMembers,
            loadPermissions,
            viewMode,
            excludeTaxonomyDefinitions,
            excludeSystemOwnedDefinitions,
            filter,
            definitionsToLoadByName,
            definitionsToLoadById,
            excludeLightDefinitions
        );

        return allEntityDefinitions;
    }

    async getAsync(
        param: string | number,
        includeConditionalMembers: boolean = true,
        loadPermissions?: boolean,
        cacheSignature?: string
    ): NullableResultPromise<IEntityDefinition> {
        Guard.notNullOrUndefined(param);

        const link: Link = await this._client.linkHelper.definitionToLinkAsync(
            param,
            includeConditionalMembers,
            loadPermissions
        );

        const uri = new URI(link.href);

        if (cacheSignature && !uri.hasQuery("after")) {
            uri.addQuery("after", cacheSignature);

            link.href = uri.toString();
        }

        return this.getDefinitionAsync(link);
    }

    async getCachedAsync(
        param: string | number,
        includeConditionalMembers: boolean = true,
        loadPermissions?: boolean,
        cacheSignature?: string
    ): NullableResultPromise<IEntityDefinition> {
        Guard.notNullOrUndefined(param);

        let definitionPromise = this._cache.getAsync(param, includeConditionalMembers, loadPermissions);

        if (!definitionPromise) {
            definitionPromise = this.getAsync(param, includeConditionalMembers, loadPermissions, cacheSignature);
            void this._cache.loadAsync(param, definitionPromise, includeConditionalMembers, loadPermissions);
        }

        return definitionPromise;
    }

    async getNameAsync(id: number, cacheSignature?: string): NullableResultPromise<string> {
        Guard.validId(id);

        const definition = await this.getAsync(id, undefined, undefined, cacheSignature);

        return definition && definition.name;
    }

    async getNameCachedAsync(id: number, cacheSignature?: string): NullableResultPromise<string> {
        Guard.validId(id);

        let definition = this._cache.get(id);

        if (!definition) {
            definition = await this.getAsync(id, undefined, undefined, cacheSignature);
        }

        return definition && definition.name;
    }

    async getIdAsync(name: string, cacheSignature?: string): NullableResultPromise<number> {
        Guard.stringNotNullOrEmpty(name);

        const definition = await this.getAsync(name, undefined, undefined, cacheSignature);

        return definition && definition.id!;
    }

    async getIdCachedAsync(name: string, cacheSignature?: string): NullableResultPromise<number> {
        Guard.stringNotNullOrEmpty(name);

        let definition = this._cache.get(name);

        if (!definition) {
            definition = await this.getAsync(name, undefined, undefined, cacheSignature);
        }

        return definition && definition.id!;
    }

    async getManyAsync(
        param: Array<string> | Array<number>,
        includeConditionalMembers: boolean = true,
        loadPermissions?: boolean
    ): Promise<Array<IEntityDefinition>> {
        Guard.notNullOrUndefined(param);

        if (param.length === 0) {
            return [];
        } else if (TypeGuards.isStringArray(param)) {
            Guard.arrayNoneNullOrEmptyString(param);

            const linkFactory: (batch: Array<string>, batchSize: number) => Promise<Link> = (
                batch,
                batchSize
            ): Promise<Link> =>
                this._client.linkHelper.definitionsToLinkAsync(
                    batch,
                    undefined,
                    batchSize,
                    includeConditionalMembers,
                    undefined,
                    loadPermissions
                );

            return this.getDefinitionsBatchedAsync(param, linkFactory);
        } else {
            Guard.validIds(param);

            const linkFactory: (batch: Array<number>, batchSize: number) => Promise<Link> = (
                batch,
                batchSize
            ): Promise<Link> =>
                this._client.linkHelper.definitionsToLinkAsync(batch, undefined, batchSize, includeConditionalMembers);

            return this.getDefinitionsBatchedAsync(param, linkFactory);
        }
    }

    async getManyCachedAsync(
        param: Array<string> | Array<number>,
        includeConditionalMembers: boolean = true,
        loadPermissions?: boolean
    ): Promise<Array<IEntityDefinition>> {
        Guard.notNullOrUndefined(param);

        if (param.length === 0) return [];

        const shouldGetByName = typeof param[0] === "string";

        const stillToFetch: Array<string | number> = [];
        const result: Array<Promise<Array<IEntityDefinition> | IEntityDefinition | null>> = [];

        for (const nameOrId of param) {
            const definition = this._cache.getAsync(nameOrId, includeConditionalMembers, loadPermissions);

            if (definition != null) {
                result.push(definition);
            } else {
                stillToFetch.push(nameOrId);
            }
        }

        if (stillToFetch != null && stillToFetch.length > 0) {
            const set = new Set(stillToFetch);
            const fetchedDefinitions = this.getManyAsync(
                shouldGetByName ? ([...set] as Array<string>) : ([...set] as Array<number>),
                includeConditionalMembers,
                loadPermissions
            );
            result.push(fetchedDefinitions);
        }

        const loadedResults = await Promise.all(result);

        return loadedResults.flat().filter(definition => definition != null);
    }

    async getNamesAsync(ids: Array<number>): Promise<MapNumberTo<string>> {
        Guard.notNullOrUndefined(ids);

        if (ids.length === 0) return {};

        Guard.validIds(ids);

        const definitions = await this.getManyAsync(ids);
        const result: MapNumberTo<string> = {};

        for (const definition of definitions) {
            result[definition.id!] = definition.name;
        }

        return result;
    }

    async getNamesCachedAsync(ids: Array<number>): Promise<MapNumberTo<string>> {
        Guard.notNullOrUndefined(ids);

        if (ids.length === 0) return {};

        Guard.validIds(ids);

        const definitions = await this.getManyCachedAsync(ids);
        const result: MapNumberTo<string> = {};

        for (const definition of definitions) {
            result[definition.id!] = definition.name;
        }

        return result;
    }

    async getIdsAsync(names: Array<string>): Promise<MapStringTo<number>> {
        Guard.notNullOrUndefined(names);

        if (names.length === 0) return {};

        const definitions = await this.getManyAsync(names);
        const result: MapStringTo<number> = {};

        for (const definition of definitions) {
            result[definition.name] = definition.id!;
        }

        return result;
    }

    async getIdsCachedAsync(names: Array<string>): Promise<MapStringTo<number>> {
        Guard.notNullOrUndefined(names);

        if (names.length === 0) return {};

        const definitions = await this.getManyCachedAsync(names);
        const result: MapStringTo<number> = {};

        for (const definition of definitions) {
            result[definition.name] = definition.id!;
        }

        return result;
    }

    async saveAsync(definition: IEntityDefinition): Promise<number> {
        Guard.notNullOrUndefined(definition);

        const resource = await this._client.entityDefinitionMapper.mapEntityDefinitionResourceAsync(definition);

        let response: ResponseMessage;
        if (definition.isNew) {
            const link = await this._client.linkHelper.definitionsLinkAsync();
            response = await this._client.raw.postAsync(link.href, TaJson.serialize(resource));
        } else {
            const link = await this._client.linkHelper.definitionToLinkAsync(definition.name);
            response = await this._client.raw.putAsync(link.href, TaJson.serialize(resource));
        }

        ResponseHandler.handleErrors(response);

        let definitionId = 0;

        if (definition.isNew) {
            definitionId = (await this.getIdAsync(definition.name)) || 0;
        } else {
            definitionId = definition.id!;

            this._cache.invalidate(definitionId);
        }

        if (definitionId === 0) {
            throw new InternalError("Did not receive a valid id after saving.");
        }

        return definitionId;
    }

    async deleteAsync(param: string | number): Promise<void> {
        Guard.notNullOrUndefined(param);

        if (typeof param === "string") {
            Guard.stringNotNullOrEmpty(param);
        } else {
            Guard.validId(param);
        }

        const link = await this._client.linkHelper.definitionToLinkAsync(param);
        const response = await this._client.raw.deleteAsync(link.href);

        ResponseHandler.handleErrors(response);

        this._cache.invalidate(param);
    }

    async getRangeAsync(skip: number, take: number): Promise<IEntityDefinitionQueryResult> {
        Guard.notNegative(skip);
        Guard.notNegative(take);

        const resource = await this.getDefinitionsSkipTake(skip, take);
        const definitions = await this._client.entityDefinitionMapper.mapEntityDefinitionsAsync(resource);

        for (const definition of definitions) {
            this._cache.load(definition);
        }

        return new EntityDefinitionQueryResult(this._client, definitions, resource.totalItems || 0, skip);
    }

    async getRangeIdsAsync(skip: number, take: number): Promise<IIdQueryResult> {
        Guard.notNegative(skip);
        Guard.notNegative(take);

        const result = await this.getRangeAsync(skip, take);

        return new EntityDefinitionIdQueryResult(
            this._client,
            result.items.map(item => item.id!),
            result.totalNumberOfResults,
            skip
        );
    }

    createEntityDefinitionIterator(pageSize: number): IEntityDefinitionIterator {
        return new EntityDefinitionIterator(this._client, pageSize);
    }

    createEntityDefinitionIdIterator(pageSize: number): IIdIterator {
        return new EntityDefinitionIdIterator(this._client, pageSize);
    }

    private async getDefinitionsSkipTake(
        skip: number,
        take: number,
        includeConditionalMembers: boolean = true,
        loadPermissions?: boolean
    ): Promise<EntityDefinitionCollectionResource> {
        const link = await this._client.linkHelper.definitionsToLinkAsync(
            null,
            skip,
            take,
            includeConditionalMembers,
            undefined,
            loadPermissions
        );
        const response = await this._client.raw.getAsync(link.href);

        const collectionResource = TaJson.deserialize<EntityDefinitionCollectionResource>(
            response.content,
            EntityDefinitionCollectionResource
        );

        return collectionResource;
    }

    private async getDefinitionAsync(link: Link): NullableResultPromise<IEntityDefinition> {
        const response = await this._client.raw.getAsync(link.href);

        if (response.statusCode === StatusCodes.NOT_FOUND) {
            return null;
        }

        const resource = TaJson.deserialize<EntityDefinitionResource>(response.content, EntityDefinitionResource);

        const definition = await this._client.entityDefinitionMapper.mapEntityDefinitionAsync(resource);

        if (definition) {
            const uri = new URI(link.href);
            this._cache.load(
                definition,
                uri.hasQuery(ENTITY_DEFINITIONS.includeConditionalMembers, true),
                uri.hasQuery(ENTITY_DEFINITIONS.loadPermissions, true)
            );
        }

        return definition;
    }

    private async getDefinitionsBatchedAsync<T extends string | number>(
        definitionsToFetch: Array<T>,
        linkFactory: (batch: Array<T>, batchSize: number, includeConditionalMembers: boolean) => Promise<Link>
    ): Promise<Array<IEntityDefinition>> {
        if (!definitionsToFetch || definitionsToFetch.length === 0) {
            return [];
        }

        // We need to batch this or the backend will refuse to respond.
        const batches = chunk<T>(definitionsToFetch, EntityDefinitionsClient._batchSize);
        const includeConditionalMembers = true;
        const definitions: Array<Promise<IEntityDefinition | null>> = [];

        for (const definitionBatch of batches) {
            const batchResult = linkFactory(
                definitionBatch,
                EntityDefinitionsClient._batchSize,
                includeConditionalMembers
            )
                .then(link => {
                    return this._client.raw.getAsync(link.href);
                })
                .then(response => {
                    ResponseHandler.handleErrors(response);

                    const resource = TaJson.deserialize<EntityDefinitionCollectionResource>(
                        response.content,
                        EntityDefinitionCollectionResource
                    );

                    return resource.items;
                });

            // Map each definition key to its result and load into cache individually
            for (const definitionKey of definitionBatch) {
                const definition = batchResult
                    .then(
                        result =>
                            result.find(definition =>
                                typeof definitionKey === "string"
                                    ? definition.name === definitionKey
                                    : definition.id === definitionKey
                            ) ?? null
                    )
                    .then(resource =>
                        resource ? this._client.entityDefinitionMapper.mapEntityDefinitionAsync(resource) : null
                    );

                definitions.push(definition);

                // Important this caching is done before any promise is resolved, to cache the load ASAP.
                void this._cache.loadAsync(definitionKey, definition, includeConditionalMembers);
            }
        }

        return Promise.all(definitions).then(results =>
            results.filter((result): result is IEntityDefinition => result != null)
        );
    }

    private async getAllBatchedAsync(
        includeConditionalMembers: boolean = true,
        loadPermissions?: boolean,
        viewMode?: ViewMode,
        excludeTaxonomyDefinitions?: boolean,
        excludeSystemOwnedDefinitions?: boolean,
        filter?: string,
        definitionsToLoadByName?: string,
        definitionsToLoadById?: string,
        excludeLightDefinitions?: boolean
    ): Promise<Array<IEntityDefinition>> {
        const resources: Array<EntityDefinitionResource> = [];

        let resource: Nullable<EntityDefinitionCollectionResource> = null;
        let skip = 0;

        while (resource == null || resource.next) {
            const link = (resource?.next ??
                (await this._client.linkHelper.definitionsLinkAsync(
                    skip,
                    EntityDefinitionsClient._batchSize,
                    includeConditionalMembers,
                    viewMode,
                    loadPermissions,
                    excludeTaxonomyDefinitions,
                    excludeSystemOwnedDefinitions,
                    filter,
                    definitionsToLoadByName,
                    definitionsToLoadById,
                    excludeLightDefinitions
                ))) as Link;
            const response = await this._client.raw.getAsync(link.href);

            ResponseHandler.handleErrors(response);

            resource = TaJson.deserialize<EntityDefinitionCollectionResource>(
                response.content,
                EntityDefinitionCollectionResource
            );
            resources.push(...resource.items);

            skip += resource.returnedItems ?? resource.items.length;
        }

        const definitions = await this._client.entityDefinitionMapper.mapEntityDefinitionsAsync(resources);

        for (const definition of definitions) {
            this._cache.load(definition);
        }

        return definitions;
    }
}
